import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Sparkles } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import axios from 'axios';
import './DataBot.css';

const DataBot = ({ isProAccount, isFounder, isOpen, setIsOpen, onOpen }) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPrompts, setShowPrompts] = useState(true);
    const messagesEndRef = useRef(null);
    const { getToken } = useAuth();
    const { username } = useRevit();
    const [conversationId, setConversationId] = useState(null);
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    const suggestedPrompts = [
        "What are my top performing resources?",
        "Which offers generate the most revenue?",
        "Analyze my recent sales trends",
        "What's my overall performance summary?",
        "How can I get more sales?",
        "How can i get more emails?",
        "What type of content is making me the most revenue?"
    ];

    const handlePromptClick = (prompt) => {
        setInputValue(prompt);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [isOpen]);

    const formatMessage = (content) => {
        if (!content) return '';
        
        let formatted = content.replace(/###\s+(.*?)($|\n)/g, '<h3>$1</h3>$2');
        formatted = formatted.replace(/##\s+(.*?)($|\n)/g, '<h2>$1</h2>$2');
        formatted = formatted.replace(/#\s+(.*?)($|\n)/g, '<h1>$1</h1>$2');
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
        formatted = formatted.replace(/- (.*?)($|\n)/g, '<li>$1</li>$2');
        formatted = formatted.replace(/\n\n/g, '<br/><br/>');
        formatted = formatted.replace(/\n/g, '<br/>');
        
        return formatted;
    };

    const toggleChat = () => {
        if (!(isProAccount || isFounder)) return;
        if (!isOpen && onOpen) {
            onOpen();
        }
        setIsOpen(!isOpen);
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const sendMessage = async () => {
        if (inputValue.trim() === '') return;
        
        const userMessage = { type: 'user', content: inputValue };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        
        setIsLoading(true);
        
        try {
            const token = await getToken();
            const response = await axios.post(`https://${SERVER_URL}/api/data-bot-reply`, {
                message: userMessage.content,
                username: username,
                conversationId: conversationId
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.data.conversationId && !conversationId) {
                setConversationId(response.data.conversationId);
            }
            
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: response.data.response,
                formattedContent: formatMessage(response.data.response)
            }]);
        } catch (error) {
            console.error('Error sending message to Data Bot:', error);
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: 'Sorry, I encountered an error. Please try again later.' 
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={`data-bot-container ${!(isProAccount || isFounder) ? 'disabled' : ''}`}>
            {isOpen && (isProAccount || isFounder) && (
                <div className="data-chat-window">
                    <div className="data-chat-header">
                        <div className="data-chat-title">
                            <Sparkles size={16} />
                            AI Coach
                        </div>
                        <button className="data-chat-close-button" onClick={toggleChat}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="data-chat-content">
                        <div className="data-chat-messages">
                            {showPrompts && (
                                <div className="data-welcome-section">
                                    <p className="data-welcome-subtitle">How can I help you?</p>
                                    <div className="data-suggested-prompts">
                                        {suggestedPrompts.map((prompt, index) => (
                                            <button
                                                key={index}
                                                className="data-prompt-button"
                                                onClick={() => handlePromptClick(prompt)}
                                            >
                                                {prompt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {messages.map((message, index) => (
                                <div key={index} className={`data-message ${message.type}`}>
                                    {message.type === 'bot' && message.formattedContent ? (
                                        <div dangerouslySetInnerHTML={{ __html: message.formattedContent }} />
                                    ) : (
                                        message.content
                                    )}
                                </div>
                            ))}
                            {isLoading && (
                                <div className="data-message bot loading">
                                    <FontAwesomeIcon icon={faSpinner} spin /> Analyzing...
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className="data-chat-input">
                            <textarea
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask about your data..."
                                disabled={isLoading}
                            />
                            <button 
                                className="data-send-button" 
                                onClick={sendMessage}
                                disabled={isLoading || inputValue.trim() === ''}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <button 
                className={`data-chat-toggle-button ${isOpen ? 'open' : ''} ${!(isProAccount || isFounder) ? 'disabled' : ''}`} 
                onClick={toggleChat}
            >
                <Sparkles size={15} />
                {!isOpen && (
                    <span>
                        AI Coach
                        {!(isProAccount || isFounder) && <span className="pro-badge">PRO</span>}
                    </span>
                )}
            </button>
        </div>
    );
};

export default DataBot;
